/**
 * Signature state helpers shared by every surface that displays or acts on a
 * stored signed document.
 *
 * A stored document resolves to exactly one of three states, and the whole
 * point of this module is that no surface re-derives that answer from a pile of
 * booleans — because the failure mode being fixed here is a document that was
 * never cryptographically verified being labelled "firma digitale valida".
 *
 * Mirrors `backend/src/routes/proposals.js`: SIGNATURE_GATES states what each
 * state allows, `signatureStateOf` resolves a record, and both fall back to
 * `unbound` rather than to a permission. The backend is the authority; nothing
 * here may be more permissive than that table.
 *
 * Two shapes carry a state to the UI:
 * - `customerSignatureState` / `finalSignatureState` on a proposal row — a
 *   state string, or `null` when no document of that kind is stored;
 * - a stored signature record (`/signature-info`, and the customer portal's
 *   projected list rows), which carries the diagnostics as well as the state.
 *
 * @module components/signatureState
 */

/**
 * The three states a stored signed document can be in.
 *
 * - `bound`   the certificate chain verified against the trust list, the signed
 *             payload digests to the PDF we sent, the signature explicitly
 *             covers the whole file, and no signer's tax identifier conflicts
 *             with the customer's — with a positive match additionally required
 *             when the customer is a natural person (mirrors
 *             resolveSignatureState in backend/src/services/signatureVerifier.js).
 *             It still does NOT say the rendered document is the one we sent:
 *             the digest match is against the file's first revision, and
 *             `docs/design/pades-base-revision-binding-gap.md` tracks that gap.
 *             The chain may have verified at today's date or at the instant a
 *             trusted timestamp says the signature was made — a certificate
 *             that was valid when it signed signed validly — and
 *             `chainVerifiedAtSigningTime` says which.
 * - `unbound` structurally a signature, but one of the above failed: the chain
 *             could not be verified, the payload does not match the original,
 *             the coverage is partial or was never established, or the signer
 *             conflicts with the customer. Stored and shown, never claimed.
 *             Both acts need an attestation first.
 * - `manual`  an `.eml` or a scan uploaded by an internal user. Never a digital
 *             signature at all: it confirms a proposal without an attestation,
 *             and it cannot be counter-signed by anyone.
 *
 * @type {Readonly<{BOUND: 'bound', UNBOUND: 'unbound', MANUAL: 'manual'}>}
 */
export const SIGNATURE_STATES = Object.freeze({
  BOUND: 'bound',
  UNBOUND: 'unbound',
  MANUAL: 'manual'
});

/**
 * What each state allows at the two acts that turn a document into a contract.
 *
 * The same table `backend/src/routes/proposals.js` reads at both decision
 * points. Keeping it here rather than re-deriving per call site is the point:
 * the two acts had already drifted apart once on the server.
 *
 * @type {Readonly<Record<string, {countersignable: boolean, requiresAttestation: boolean}>>}
 */
export const SIGNATURE_GATES = Object.freeze({
  [SIGNATURE_STATES.BOUND]: Object.freeze({ countersignable: true, requiresAttestation: false }),
  [SIGNATURE_STATES.UNBOUND]: Object.freeze({ countersignable: true, requiresAttestation: true }),
  [SIGNATURE_STATES.MANUAL]: Object.freeze({ countersignable: false, requiresAttestation: false })
});

/**
 * The gate for one state.
 *
 * An unrecognised state — one written by a newer backend than this bundle —
 * falls back to the `unbound` row, so a label this file has never heard of
 * gains no permission by default. `null` (no document at all) lands there too;
 * callers that need to distinguish "nothing stored" test for it before asking.
 *
 * @param {string|null|undefined} state
 * @returns {{countersignable: boolean, requiresAttestation: boolean}}
 */
export function signatureGate(state) {
  return SIGNATURE_GATES[state] || SIGNATURE_GATES[SIGNATURE_STATES.UNBOUND];
}

/**
 * Whether acting on a document in this state requires an explicit attestation.
 *
 * @param {string|null|undefined} state
 * @returns {boolean}
 */
export function requiresAttestationFor(state) {
  return signatureGate(state).requiresAttestation;
}

/**
 * Whether a document in this state can be counter-signed at all.
 *
 * `manual` cannot: a qualified signature over a scan would assert that a
 * picture of a signature is a qualified one, and no attestation makes that
 * true. The UI must present that as an explanation, never as a box to tick.
 *
 * @param {string|null|undefined} state
 * @returns {boolean}
 */
export function isCountersignable(state) {
  return signatureGate(state).countersignable;
}

/**
 * Collapse any state value to one this file knows how to render.
 *
 * `null`/`undefined` pass through as `null`, meaning "no document stored" — the
 * one case that is genuinely not a state. Everything else that is not a known
 * label reads as `unbound`, matching {@link signatureGate}: an unknown label is
 * never rendered as verified, and never as an absent document either.
 *
 * @param {string|null|undefined} state
 * @returns {'bound'|'unbound'|'manual'|null}
 */
export function normalizeSignatureState(state) {
  if (state === null || state === undefined) return null;
  return SIGNATURE_GATES[state] ? /** @type {'bound'|'unbound'|'manual'} */ (state) : SIGNATURE_STATES.UNBOUND;
}

/**
 * Resolve the state of a stored signature record.
 *
 * Returns `null` only when there is no record at all, which for a record-shaped
 * caller means "no document to describe". A record that exists but carries no
 * `state` field predates verification and is read as `unbound` rather than
 * assumed verified; a pre-state manual upload is still recognised by its
 * `manualConfirmation` flag, so the owner's own path keeps its own label.
 *
 * @param {Object|null|undefined} info Stored signature info
 * @returns {'bound'|'unbound'|'manual'|null}
 */
export function signatureStateOf(info) {
  if (!info || typeof info !== 'object') return null;
  if (info.state) return normalizeSignatureState(info.state);
  if (info.manualConfirmation) return SIGNATURE_STATES.MANUAL;
  return SIGNATURE_STATES.UNBOUND;
}

/**
 * The specific things that could not be verified about a document, as i18n key
 * suffixes under `signatureState.reasons.*`.
 *
 * "The chain was not verified" and "the signed content does not match the PDF
 * we sent" are different worries that send the reader to different checks, so
 * they are never collapsed into one generic "sei sicuro?". Fields the
 * customer-facing projection strips (`trustAnchorConfigured`,
 * `trustAnchorUnreadable`, `bindingReason`) degrade to the generic wording
 * rather than to a guess. The signer-match fields are not among them: a
 * customer is told whether their own document was signed by their own
 * certificate, and told on what basis.
 *
 * @param {Object|null|undefined} info Stored signature info
 * @returns {string[]} Reason keys; empty unless the document is `unbound`
 */
export function signatureUnverifiedReasons(info) {
  if (signatureStateOf(info) !== SIGNATURE_STATES.UNBOUND) return [];
  // No `state` field at all: stored before verification existed, so nothing
  // about it was ever checked. Saying which check failed would be an invention.
  if (!info.state) return ['unknown'];

  // A state with none of the diagnostic fields is a state we were told (a
  // server refusal, or a proposal row), not a record we read. Every real record
  // — including the customer-facing projection — carries both booleans, so
  // naming a cause here would be inventing one.
  if (info.chainVerified === undefined && info.boundToOriginal === undefined) return ['detailUnavailable'];

  const reasons = [];
  if (info.chainVerified !== true) {
    // Five different sentences, because they send different people to different
    // places: nothing configured (the operator has not set a trust list yet),
    // configured but unreadable (the operator's path is wrong — a *server*
    // problem wearing a document's clothes), a timestamp that could not be
    // checked, a certificate that had simply run out with nothing but the
    // signer's own word about when they signed, and the real one, the signer's
    // certificate not being on the list. The customer-facing projection strips
    // the operator fields and the timestamp object, so a customer keeps the
    // wording that describes their own document.
    if (info.trustAnchorConfigured === false) reasons.push('noTrustBundle');
    else if (info.trustAnchorUnreadable === true) reasons.push('trustBundleUnreadable');
    else if (info.timestamp?.present === true && info.timestamp.verified !== true) reasons.push('timestampUnverified');
    // An expired certificate is not an untrusted one. The signature may have
    // been perfectly valid when it was made — there is simply nothing here to
    // prove when that was, because `signingTime` is the signer's own claim.
    else if (info.certificateExpired === true) reasons.push('expiredCertificate');
    else reasons.push('chain');
  }
  if (info.boundToOriginal !== true) {
    if (info.bindingReason === 'digest-mismatch') reasons.push('digestMismatch');
    else if (info.bindingReason === 'no-stored-digest') reasons.push('noStoredDigest');
    else if (info.bindingReason === 'payload-missing' || info.bindingReason === 'payload-unavailable') reasons.push('payloadUnavailable');
    else reasons.push('notBound');
  }
  // Coverage is tri-state and only an explicit yes clears the gate, so the two
  // failures are two sentences: a signature that demonstrably covers part of the
  // file, and one whose coverage nothing established. Collapsing them would tell
  // an operator that changes were appended when nobody knows that.
  if (info.coversWholeDocument === false) reasons.push('partialCoverage');
  else if (info.coversWholeDocument === null) reasons.push('coverageUnknown');
  // Who signed, which is the newest way a document can be unverified and the one
  // an operator can least afford to read as a generic failure. A conflict is a
  // statement about the wrong party; a natural person the comparison could not
  // place is an absence of evidence about the right one; a customer the system
  // could not place at all is an absence of the question. All three block, and
  // they send the reader to three different places.
  if (info.customerSignatureMatch === false) reasons.push('signerMismatch');
  else if (info.customerSignatureSubject === 'natural-person' && info.customerSignatureMatch !== true) {
    reasons.push('signerUnidentified');
  } else if (info.customerSignatureSubject === 'unknown') {
    // Not a doubt about the signer: a doubt about the customer record the signer
    // was supposed to be compared against. It sends the reader to the anagrafica
    // or to whoever administers the installation, never to the person who
    // signed, so it cannot be folded into `signerUnidentified`.
    reasons.push('customerUnidentified');
  }
  return reasons.length > 0 ? reasons : ['unknown'];
}

/**
 * The i18n key naming a state, for a badge or a table cell.
 *
 * Only `bound` and `manual` get their own label; an unknown state is labelled
 * as unverified, and only a genuinely absent document is labelled as absent.
 *
 * @param {string|null|undefined} state
 * @returns {string}
 */
export function signatureStateLabelKey(state) {
  const normalized = normalizeSignatureState(state);
  if (normalized === SIGNATURE_STATES.BOUND) return 'signatureState.bound';
  if (normalized === SIGNATURE_STATES.MANUAL) return 'signatureState.manual';
  if (normalized === SIGNATURE_STATES.UNBOUND) return 'signatureState.unbound';
  return 'signatureState.none';
}
